import { Pad } from 'component/basics';
import { Heading } from 'component/text';
import { useVideoVotingResults } from 'data/video-votings';
import { useVideo } from 'open-modules/zdf/component/videoplayer/videoplayer-state';
import {
    updateVideoVotingSessionData,
    useVideoVotingSessionData,
    VIDEO_VOTING_SESSION_STATES,
} from 'open-modules/zdf/videovoting/videoVotingSession';
import { exitFullScreen } from 'platform-specific/zdf/exitFullScreen';
import React, { useEffect } from 'react';
import type { ViewStyle } from 'react-native';
import { useDatastore } from 'util/datastore';

import { VideoVotingCountdownOverlay } from './videovoting-countdown-overlay';
import { VideoVotingOverlay } from './videovoting-overlay';
import { VideoVotingQuestionEstimationDialog } from './videovoting-question-estimation-dialog';
import { VideoVotingQuestionPollDialog } from './videovoting-question-poll-dialog';
import type { VideoVotingQuestion, VideoVotingQuestionEstimation, VideoVotingQuestionPoll } from './videovoting-types';
import { logQuestionDialogEvent, useVideoVoting } from './videovoting-util';

type VideoVotingQuestionDialogProps = {
    question: VideoVotingQuestion;
};

export function VideoVotingQuestionDialog({ question }: VideoVotingQuestionDialogProps) {
    const { pause } = useVideo();
    const countdownPadding = 16;
    // The session names the voting, so the vote read does not wait on a lookup
    // to learn its key.
    const session = useVideoVotingSessionData();

    const { prompts } = useVideoVotingResults(session?.votingKey);
    const results = prompts.find((prompt) => prompt.questionKey === question.key);
    const ownVote = results?.mine ?? null;

    // If the question has stopsVideo property pause the current video playback
    useEffect(() => {
        if (question.stopsVideo) {
            pause?.();
        }
    }, [pause, question.stopsVideo]);

    return (
        <VideoVotingOverlay
            style={{
                width: '45%',
                minWidth: question.type === 'slider' ? 400 + countdownPadding : 335 + countdownPadding,
                maxWidth: 645 + countdownPadding,
                minHeight: question.type === 'slider' ? 225 + countdownPadding : 250 + countdownPadding,
                maxHeight: 460 + countdownPadding,
                // The wire carries each offset as a number of pixels or a
                // percentage string, which is wider than the `DimensionValue`
                // React Native names but is the same set of values it accepts.
                ...(question.position as ViewStyle),
            }}
        >
            <VideoVotingQuestionDialogCountdownWrapper question={question}>
                <Heading numberOfLines={2} text={question.text} />
                <Pad size={24} />
                {question.type === 'slider' ? (
                    <VideoVotingQuestionEstimationDialog ownVote={ownVote} question={question as VideoVotingQuestionEstimation} />
                ) : (
                    <VideoVotingQuestionPollDialog ownVote={ownVote} question={question as VideoVotingQuestionPoll} results={results} />
                )}
            </VideoVotingQuestionDialogCountdownWrapper>
        </VideoVotingOverlay>
    );
}

type VideoVotingQuestionDialogCountdownWrapperProps = {
    children: React.ReactNode;
    question: VideoVotingQuestion;
};
function VideoVotingQuestionDialogCountdownWrapper({ children, question }: VideoVotingQuestionDialogCountdownWrapperProps) {
    const datastore = useDatastore();
    const session = useVideoVotingSessionData();
    const videoVoting = useVideoVoting();
    const video = useVideo();

    const onCloseQuestionDialog = () => {
        const questionSessionState = session?.questions?.[question.key];
        const updatedQuestions = { ...session.questions };
        updatedQuestions[question.key] = { ...questionSessionState, answered: true };

        updateVideoVotingSessionData(datastore, {
            state: VIDEO_VOTING_SESSION_STATES.waitForQuestion,
            questions: updatedQuestions,
        });

        video?.play?.();
    };

    const onPressContinueButton = () => {
        logQuestionDialogEvent({ datastore, eventType: 'videovoting-skip-remaining-time', question });
        onCloseQuestionDialog();
    };

    const onTimeUp = () => {
        logQuestionDialogEvent({ datastore, eventType: 'videovoting-time-up', question });
        onCloseQuestionDialog();
    };

    const openDiscussion = () => {
        logQuestionDialogEvent({ datastore, eventType: 'videovoting-navigate-to-discussion', question });
        exitFullScreen();
        if (videoVoting?.linkedSpaceKey) {
            datastore.gotoInstance({
                structureKey: 'question',
                instanceKey: videoVoting?.linkedSpaceKey,
            });
        }
    };

    return (
        <VideoVotingCountdownOverlay onClickDiscourse={openDiscussion} onSkip={onPressContinueButton} onTimeUp={onTimeUp} timer={60}>
            {children}
        </VideoVotingCountdownOverlay>
    );
}
